import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageLoader, PageHeader, StageIcon } from "@/components/shared";
import { Disclaimer } from "@/components/Disclaimer";
import { CheckCircle2, Circle, BadgeCheck, ArrowRight } from "lucide-react";

const CONTINGENCIES = [
  "Financing contingency (your loan is confirmed in writing)",
  "Home inspection contingency",
  "Appraisal contingency",
  "Clear understanding of closing costs and timeline",
];

export default function OfferReadiness() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["workspace"], queryFn: async () => (await api.get("/buyer/workspace")).data });
  if (isLoading) return <PageLoader />;

  const { roadmap, client } = data;
  const checklist = client.profile ? client.checklist : client.checklist;
  const basics = [
    { label: "Mortgage pre-approval", done: !!client.checklist?.preapproval?.done },
    { label: "Budget clarity", done: !!client.checklist?.budget_clarity?.done },
    { label: "Documents ready", done: !!client.checklist?.documents?.done },
    { label: "Compared at least 2 homes", done: roadmap.homes_count >= 2 },
  ];
  const ready = roadmap.offer_ready;

  return (
    <div>
      <PageHeader title="Offer readiness" subtitle="A transparent view of whether you've completed the basics. This does not recommend a price or any legal terms — your agent and attorney handle those." />

      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
        <Card className={`p-7 rounded-2xl mb-6 ${ready ? "bg-status-complete/10 border-status-complete/30" : "bg-secondary/60"}`} data-testid="offer-readiness-status">
          <div className="flex items-center gap-4">
            <div className={`h-14 w-14 rounded-2xl flex items-center justify-center ${ready ? "bg-status-complete text-white" : "bg-muted text-muted-foreground"}`}>
              <BadgeCheck className="h-7 w-7" />
            </div>
            <div>
              <p className="font-heading text-xl font-bold">{ready ? "You've completed the basics" : "A few basics still to go"}</p>
              <p className="text-muted-foreground text-sm mt-0.5">
                {ready ? "You appear ready to discuss making an offer with your agent." : "Finish the items below, then talk to your agent about next steps."}
              </p>
            </div>
          </div>
        </Card>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6 rounded-2xl">
          <h3 className="font-heading font-semibold text-lg mb-4">Your basics</h3>
          <div className="space-y-3">
            {basics.map((b) => (
              <div key={b.label} className="flex items-center gap-3" data-testid={`offer-basic-${b.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                {b.done ? <CheckCircle2 className="h-5 w-5 text-status-complete" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
                <span className={b.done ? "" : "text-muted-foreground"}>{b.label}</span>
              </div>
            ))}
          </div>
          {!ready && (
            <Button onClick={() => navigate("/app/checklist")} variant="outline" className="w-full mt-5 rounded-full gap-2" data-testid="finish-basics-btn">
              Finish my readiness <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </Card>

        <Card className="p-6 rounded-2xl">
          <h3 className="font-heading font-semibold text-lg mb-4">Common contingencies to discuss</h3>
          <ul className="space-y-3">
            {CONTINGENCIES.map((c) => (
              <li key={c} className="text-sm text-muted-foreground flex gap-2"><span className="text-forest mt-1">•</span>{c}</li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground mt-4">These are general concepts to talk through with your agent — not legal advice.</p>
        </Card>
      </div>

      <div className="mt-6">
        <Disclaimer>Offer readiness only reflects whether you've completed your own checklist items. Look Before You Leap 808 never recommends an offer price, terms, or whether to buy. Always work with your agent and a licensed real estate attorney.</Disclaimer>
      </div>
    </div>
  );
}
